/**
 * Customer API Types
 *
 * FE uses nested structure matching BE exactly.
 * BE returns { persons: Person[], companies: CompanyWithContact[] }
 *
 * Field naming: BE uses camelCase (firstName, streetNumber, cityPostCode)
 */

import type { EurodriveDocument } from '../../types/documents'

// Enum Types
export type CustomerType = 'PERSON' | 'COMPANY'
export type InvoiceType = 'RENT' | 'POLICE_TICKET' | 'INSURANCE_LIABILITY'
export type PayerType = 'insurance' | 'company' | 'person'
export type PortionType = 'taxBase' | 'vat' | 'full'

/**
 * Person entity - matches BE Person structure.
 * The contactPerson* flat fields hold the optional "Kontaktní osoba" data
 * (= the slim secondary contact for company customers).
 */
export interface Person {
  id: string
  firstName: string
  surname: string
  birthNumber: string
  identificationCardNumber?: string
  driverLicence?: string
  street: string
  streetNumber: string
  city: string
  cityPostCode: string
  telephone: string
  email: string
  description?: string
  // Optional "Kontaktní osoba" — stored as flat fields on the Person entity.
  hasContactPerson?: boolean
  contactPersonFirstName?: string
  contactPersonSurname?: string
  contactPersonTelephone?: string
  contactPersonEmail?: string
  // Audit fields from BE
  created?: number
  changed?: number
  createdBy?: string
  changedBy?: string
  ownedBy?: string
  customData?: Record<string, unknown>
}

/**
 * Company entity - matches BE Company structure
 */
export interface Company {
  id: string
  name: string
  companyBusinessNumber: string
  taxPayer: boolean
  street: string
  streetNumber: string
  city: string
  cityPostCode: string
  telephone: string
  email: string
  description?: string
  // Audit fields from BE
  created?: number
  changed?: number
  createdBy?: string
  changedBy?: string
  ownedBy?: string
  customData?: Record<string, unknown>
}

/**
 * DocumentEvent entity - links CustomerInvoice to SDE Content (document)
 * Stored in BE as separate entity in io.alture.eurodrive.entities.customers.documentevents
 */
export interface DocumentEvent {
  id: string
  invoiceId: string           // References CustomerInvoice.id
  entityId: string            // References SDE Content UUID (the document file)
  customData?: {
    documentType?: string     // e.g., 'faktura-pujcky', 'pohledavka-pokuta'
    fileName?: string
    uploadedAt?: string
    [key: string]: unknown
  }
  // Audit fields from BE
  created?: number
  changed?: number
}

/**
 * Payment portion - tracks payment status for a single payer's portion of an invoice.
 *
 * For COMPANY customers, an invoice is split into:
 * - taxBase (základ daně) — paid by insurance
 * - vat (DPH) — paid by the company
 * Credit notes always reduce the taxBase portion.
 *
 * For PERSON customers, a single 'full' portion is used.
 */
export interface PaymentPortion {
  type: PortionType
  label: string                    // 'Základ daně' | 'DPH' | 'Celkem'
  amount: number                   // Portion amount
  paid: boolean
  datePaid?: string
  paidAmount?: number              // Actual amount received (for underpayment tracking)
  payerType: PayerType
  paymentRequestDates?: string[]   // Independent request tracking per portion
  // Credit note (only on taxBase portions)
  creditNoteAmount?: number
  creditNoteNumber?: string
  creditNoteReason?: string
}

/**
 * Customer Invoice entity
 *
 * Customer is the main container of invoices - all invoices (rental, fines, etc.)
 * are stored on the customer level and referenced from rental cases.
 *
 * Fields:
 * - cost: Total invoiced amount (incl. VAT)
 * - paid: Derived — true when all payments[] portions are paid
 * - payments: Always populated — tracks per-payer payment status
 */
export interface CustomerInvoice {
  id: string
  invoiceId: string
  invoiceType: InvoiceType
  cost: number
  paid: boolean
  customData?: {
    amountWithoutVat?: number
    vatRate?: number
    vatAmount?: number
    payments?: PaymentPortion[]
    invoiceNumber?: string
    varSymbol?: string
    invoiceDate?: string
    dueDate?: string
    fineDate?: string
    relatedCaseId?: string
    relatedInsuranceEventId?: string
    source?: string
    notes?: string
    contentId?: string
    [key: string]: unknown
  }
}

/**
 * Customer entity - unified FE type matching BE structure
 * - person: Always populated (for PERSON = the person, for COMPANY = the responsible/main person)
 * - company: Only populated for COMPANY type
 * The optional "Kontaktní osoba" lives in the contactPerson* flat fields on Person.
 */
export interface Customer {
  id: string
  type: CustomerType
  // Person data (always — for COMPANY this is the responsible/main person)
  person?: Person
  // Company data (for COMPANY type customers)
  company?: Company
  // FE-only fields
  rentalCaseId?: string | null
  carType?: string
  carLicensePlate?: string
  carManufactureDate?: string
  invoices?: CustomerInvoice[]
  groupId?: string | null 
  // Custom data stored via BE (documents, container reference, etc.)
  customData?: {
    containerId?: string
    documents?: EurodriveDocument[]
    [key: string]: unknown
  }
}

/**
 * Update customer request
 */
export type UpdateCustomerRequest = Partial<Omit<Customer, 'id' | 'invoices'>>

/**
 * Create invoice request
 */
export interface CreateInvoiceRequest {
  invoiceId: string
  invoiceType: InvoiceType
  cost: number
  paid: boolean
}

/**
 * Update invoice request
 */
export type UpdateInvoiceRequest = Partial<Omit<CustomerInvoice, 'id'>>

/**
 * Create empty person
 */
export const createEmptyPerson = (): Person => ({
  id: '',
  firstName: '',
  surname: '',
  birthNumber: '',
  identificationCardNumber: '',
  driverLicence: '',
  street: '',
  streetNumber: '',
  city: '',
  cityPostCode: '',
  telephone: '',
  email: '',
  description: ''
})

/**
 * Create empty company
 */
export const createEmptyCompany = (): Company => ({
  id: '',
  name: '',
  companyBusinessNumber: '',
  taxPayer: false,
  street: '',
  streetNumber: '',
  city: '',
  cityPostCode: '',
  telephone: '',
  email: '',
  description: ''
})

/**
 * Create empty customer (for new customer form)
 */
export const createEmptyCustomer = (): Customer => ({
  id: '',
  type: 'PERSON',
  person: createEmptyPerson(),
  invoices: [],
  groupId: null
})

/**
 * Create empty invoice
 */
export const createEmptyInvoice = (): CustomerInvoice => ({
  id: '',
  invoiceId: '',
  invoiceType: 'RENT',
  cost: 0,
  paid: false,
  customData: {
    payments: [{ type: 'full', label: 'Celkem', amount: 0, paid: false, payerType: 'person' }]
  }
})

/**
 * Create empty document event
 */
export const createEmptyDocumentEvent = (): DocumentEvent => ({
  id: '',
  invoiceId: '',
  entityId: ''
})

/**
 * Helper to get customer display name
 * For COMPANY: shows company name with responsible person in parentheses
 * For PERSON: shows person's full name
 */
export const getCustomerDisplayName = (customer: Customer): string => {
  if (customer.type === 'COMPANY' && customer.company?.name) {
    // Use person (responsible person) for display, not contactPerson
    const responsiblePerson = customer.person
    const personName = responsiblePerson
      ? `${responsiblePerson.firstName} ${responsiblePerson.surname}`.trim()
      : ''
    return personName ? `${customer.company.name} (${personName})` : customer.company.name
  }
  if (customer.person) {
    return `${customer.person.firstName} ${customer.person.surname}`
  }
  return 'Neznámý zákazník'
}

/**
 * Helper to get full name (always from person - the responsible person)
 */
export const getCustomerFullName = (customer: Customer): string => {
  if (customer.person) {
    return `${customer.person.firstName} ${customer.person.surname}`
  }
  return ''
}

/**
 * Helper to check if customer is a company
 */
export const isCompanyCustomer = (customer: Customer): boolean => {
  return customer.type === 'COMPANY'
}
